"use client";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Finn } from "@/app/components/Finn";
import { CLAY_PILL } from "@/app/components/orbitButtons";
import {
  AnimatedIcon,
  type AnimatedIconData,
} from "@/app/components/AnimatedIcon";
import { ARROW_ICON } from "@/app/components/animatedIcons";

// Example Swiss business URLs for typewriter effect
const EXAMPLE_URLS = [
  "www.schreinerei-meier.ch",
  "www.garage-mueller.ch",
  "www.baeckerei-huber.ch",
  "www.malerei-schmid.ch",
];

interface CompanyLinkInputProps {
  className?: string;
  onSubmit?: (companyUrl: string) => void;
  onUrlChange?: (companyUrl: string) => void;
  showTitle?: boolean;
  initialUrl?: string;
  /** "card" is the standalone white card; "bare" is an inline pill form. */
  variant?: "card" | "bare";
  /** Colour treatment for the bare variant: on paper or on a dark section. */
  tone?: "light" | "dark";
  /** Submit button text; defaults to the job-ad CTA. */
  ctaLabel?: string;
  /** Submit button icon; also plays once in view. Defaults to the arrow. */
  ctaIcon?: AnimatedIconData;
}

export default function CompanyLinkInput({
  className = "",
  onSubmit,
  onUrlChange,
  showTitle = true,
  initialUrl = "",
  variant = "card",
  tone = "light",
  ctaLabel,
  ctaIcon,
}: CompanyLinkInputProps) {
  const isBare = variant === "bare";
  const isDark = isBare && tone === "dark";
  const { t } = useTranslation();

  const [companyUrl, setCompanyUrl] = useState(initialUrl);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingUrl, setIsCheckingUrl] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [placeholderText, setPlaceholderText] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const urlIndexRef = useRef(0);
  const charIndexRef = useRef(0);

  // Typewriter effect for placeholder
  useEffect(() => {
    if (companyUrl || isFocused) return; // Don't animate if user has typed or focused

    const currentUrl = EXAMPLE_URLS[urlIndexRef.current];
    let timeoutId: NodeJS.Timeout;

    if (isTyping) {
      // Typing phase
      if (charIndexRef.current < currentUrl.length) {
        timeoutId = setTimeout(() => {
          setPlaceholderText(currentUrl.slice(0, charIndexRef.current + 1));
          charIndexRef.current++;
        }, 80);
      } else {
        // Pause at end of word, then start deleting
        timeoutId = setTimeout(() => {
          setIsTyping(false);
        }, 2000);
      }
    } else {
      // Deleting phase
      if (charIndexRef.current > 0) {
        timeoutId = setTimeout(() => {
          charIndexRef.current--;
          setPlaceholderText(currentUrl.slice(0, charIndexRef.current));
        }, 40);
      } else {
        // Move to next URL
        urlIndexRef.current = (urlIndexRef.current + 1) % EXAMPLE_URLS.length;
        setIsTyping(true);
      }
    }

    return () => clearTimeout(timeoutId);
  }, [placeholderText, isTyping, companyUrl, isFocused]);

  // Reset typewriter when input is cleared
  useEffect(() => {
    if (!companyUrl && !isFocused) {
      charIndexRef.current = 0;
      setPlaceholderText("");
      setIsTyping(true);
    }
  }, [companyUrl, isFocused]);

  // Check if company URL is valid
  const isValidUrl = (url: string) => {
    if (!url.trim()) return false;
    try {
      const urlToCheck = url.startsWith("http") ? url : `https://${url}`;
      const urlObj = new URL(urlToCheck);
      const hostname = urlObj.hostname;

      // Hostname must exist
      if (!hostname || hostname.length === 0) return false;

      // Hostname must be:
      // - localhost, OR
      // - a valid IP address (IPv4 or IPv6), OR
      // - contain at least one dot (for domain.TLD format)
      const isLocalhost = hostname === "localhost";
      const isIPv4 = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
      const isIPv6 = hostname.includes(":");
      const hasDot = hostname.includes(".");

      return isLocalhost || isIPv4 || isIPv6 || hasDot;
    } catch {
      return false;
    }
  };

  // Normalize URL to always include protocol (defaults to https://)
  const normalizeUrl = (url: string): string => {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    // If it already starts with http:// or https://, return as-is
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed;
    }
    // Otherwise, prepend https://
    return `https://${trimmed}`;
  };

  const handleInputChange = (value: string) => {
    setCompanyUrl(value);
    onUrlChange?.(value);

    // Clear error when user starts typing
    if (error) {
      setError(false);
      setErrorMessage("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate company URL
    if (!isValidUrl(companyUrl)) {
      setError(true);
      setErrorMessage(
        t(
          "heroForm.companyUrlInvalid",
          "Please enter a valid website URL (e.g., https://example.com)"
        )
      );
      return;
    }

    // Check if the company website is reachable (catches typos)
    setIsCheckingUrl(true);
    try {
      const res = await fetch("/api/validate-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: companyUrl }),
      });
      const data = (await res.json()) as {
        reachable: boolean;
        error?: string;
      };
      if (!data.reachable) {
        setError(true);

        // Pick a specific message based on the server's error classification
        let messageKey = "heroForm.companyUrlUnreachable";
        let fallback =
          "We couldn't reach this website. Please double-check the URL for typos (e.g. missing letters or wrong domain ending like .ch vs .com).";

        if (data.error === "Domain not found") {
          messageKey = "heroForm.companyUrlDnsNotFound";
          fallback =
            'This website address doesn\'t exist. Check for typos in the domain name (e.g. "examlpe.ch" instead of "example.ch").';
        } else if (data.error === "Connection timed out") {
          messageKey = "heroForm.companyUrlTimeout";
          fallback =
            "This website took too long to respond. It might be temporarily down — you can try again or continue with a different URL.";
        }

        setErrorMessage(t(messageKey, fallback));
        return;
      }
    } catch {
      // If the check itself fails (e.g. network error), don't block submission
    } finally {
      setIsCheckingUrl(false);
    }

    setIsSubmitting(true);
    const normalizedUrl = normalizeUrl(companyUrl);
    onSubmit?.(normalizedUrl);
    // Reset submitting state after a short delay
    setTimeout(() => setIsSubmitting(false), 1000);
  };

  const companyUrlValid = isValidUrl(companyUrl);

  return (
    <div
      data-variant={variant}
      className={`${
        isBare
          ? "flex flex-col gap-3"
          : "bg-white border border-gray-100 rounded-3xl shadow-2xl shadow-blue-900/10 flex-1 p-8 sm:p-10 flex flex-col gap-6"
      } w-full self-start ${className}`}
    >
      {/* Header with Gradient Icon */}
      {showTitle && !isBare && (
        <div className="flex items-center gap-3 mb-2">
          <div className="flex-shrink-0">
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.3333 2.66602L4 18.666H14.6667L13.3333 29.3327L26.6667 13.3327H16L17.3333 2.66602Z"
                fill="url(#paint0_linear_143_29)"
                stroke="url(#paint1_linear_143_29)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_143_29"
                  x1="4"
                  y1="2.66602"
                  x2="30.3183"
                  y2="25.0366"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#024FC8" />
                  <stop offset="1" stopColor="#0F7BF6" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_143_29"
                  x1="4"
                  y1="2.66602"
                  x2="30.3183"
                  y2="25.0366"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#0350C8" />
                  <stop offset="1" stopColor="#0F7BF5" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            {t("jobForm.companyUrl.label")}
          </h2>
        </div>
      )}

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className={`w-full flex flex-col ${isBare ? "gap-3" : "gap-6"}`}
      >
        {/* Company URL Input */}
        <div className={`flex flex-col gap-2 `}>
          {isBare && (
            <label htmlFor="companyUrl" className="sr-only">
              {t("jobForm.companyUrl.label")}
            </label>
          )}
          <div className="relative">
            <input
              id="companyUrl"
              type="text"
              name="companyUrl"
              value={companyUrl}
              onChange={e => handleInputChange(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={
                placeholderText || t("jobForm.companyUrl.placeholder")
              }
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              className={`w-full h-14 pr-12 ${
                isBare
                  ? "border px-6 rounded-full text-base focus:outline-none"
                  : "border-2 px-4 rounded-xl text-base"
              } ${
                error
                  ? `border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/30 ${
                      isDark ? "bg-white/[0.04] text-white" : "bg-white"
                    }`
                  : isDark
                    ? "border-white/15 bg-white/[0.04] text-white focus:border-signal-accent focus:ring-4 focus:ring-signal-accent/25"
                    : isBare
                      ? // The URL field is a lens: clear glass, ink text, no wash.
                        "glass-pill glass-lens border-transparent text-signal-ink focus:border-signal-accent focus:ring-4 focus:ring-signal-accent/20"
                      : "border-gray-200 bg-gray-50 text-gray-700 focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/20 focus:bg-white"
              } ${
                // A lens never animates its shadow or backdrop filter.
                isBare && !isDark && !error
                  ? "transition-colors"
                  : "transition-all"
              } duration-200 ${
                isDark
                  ? "placeholder:text-white/40"
                  : isBare
                    ? "placeholder:text-signal-muted"
                    : "placeholder:text-gray-400"
              }`}
              disabled={isSubmitting}
            />
            {companyUrlValid && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            )}
          </div>
          {error && companyUrl.trim() && (
            <p
              className={`text-sm mt-1 ${
                isDark
                  ? "text-red-300 px-1"
                  : isBare
                    ? "text-red-700 px-1"
                    : "text-red-500"
              }`}
            >
              {errorMessage ||
                t(
                  "heroForm.companyUrlInvalid",
                  "Please enter a valid website URL (e.g., https://example.com)"
                )}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div className="relative">
          <button
            type="submit"
            disabled={!companyUrlValid || isSubmitting || isCheckingUrl}
            className={
              isBare
                ? `${CLAY_PILL} group/cta w-full h-14 px-7 whitespace-nowrap justify-between`
                : "w-full h-14 bg-[#007AFF] text-white font-semibold rounded-full shadow-lg shadow-blue-500/30 hover:-translate-y-0.5 hover:bg-[#0056CC] transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:bg-[#007AFF] flex items-center justify-center gap-2 max-w-[412px] mx-auto"
            }
          >
            {isCheckingUrl ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t("heroForm.companyUrlChecking", "Checking website...")}
              </span>
            ) : isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t("heroForm.submitting", "Submitting...")}
              </span>
            ) : (
              <>
                <span>
                  {ctaLabel ||
                    t("jobForm.ctaButton", "Jetzt Inserat generieren")}
                </span>
                <AnimatedIcon
                  icon={ctaIcon ?? ARROW_ICON}
                  playOnView={ctaIcon ? 350 : undefined}
                  className={`w-5 h-5 ml-1 ${
                    isBare
                      ? "transition-transform duration-300 group-hover/cta:translate-x-1"
                      : ""
                  }`}
                />
              </>
            )}
          </button>
          {/* Finn - appears when URL is valid, next to the button. Not
              rendered in the bare variant: his render would download unseen. */}
          {!isBare && (
            <div
              className={`absolute -right-16 top-1/2 -translate-y-1/2 transition-all duration-300 pointer-events-none hidden sm:block ${
                companyUrlValid && !isSubmitting
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 translate-x-4"
              }`}
            >
              <div className="relative">
                <Finn state="hello" className="w-[72px]" />
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded-lg whitespace-nowrap">
                  {t("companyInput.finnReady", "Los geht's!")}
                  <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-gray-900 rotate-45" />
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
